import { Link } from "react-router-dom";
import { Tag, Key, Briefcase, Sparkles, ShoppingBag, Handshake, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const OPTIONS = [
  {
    id: "market",
    to: "/market/new",
    icon: Tag,
    title: "Sell something you own",
    blurb: "One-off item, physical goods. Buyers pay through Hoomanz.",
    testid: "sell-hub-market",
  },
  {
    id: "product",
    to: "/profile/edit",
    icon: ShoppingBag,
    title: "Sell a digital or physical product",
    blurb: "Sold repeatedly — courses, ebooks, prints, made-to-order items.",
    testid: "sell-hub-product",
  },
  {
    id: "service",
    to: "/profile/edit",
    icon: Sparkles,
    title: "Offer a service",
    blurb: "Bookable sessions — photography, coaching, consultations.",
    testid: "sell-hub-service",
  },
  {
    id: "job",
    to: "/jobs/new",
    icon: Briefcase,
    title: "Post a job",
    blurb: "Hire another Hooman for a role — one-off gig or ongoing.",
    testid: "sell-hub-job",
  },
  {
    id: "rental",
    to: "/rentals/new",
    icon: Key,
    title: "Rent something out",
    blurb: "Rooms, cars, gear — priced by hour, day, week or month.",
    testid: "sell-hub-rental",
  },
  {
    id: "collab",
    to: "/collabs/new",
    icon: Handshake,
    title: "Find a collaborator",
    blurb: "Team up with another Hooman on a project — Tech, Art, Events and more.",
    testid: "sell-hub-collab",
  },
];

export default function Sell() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-14" data-testid="sell-hub">
      <p className="overline mb-2">Sell on Hoomanz</p>
      <h1 className="h-display text-4xl sm:text-5xl">What would you like to sell today?</h1>
      <p className="text-[#454545] mt-3 max-w-xl">Every option here is paid securely through Hoomanz. You keep 100 % of your listed price — buyers pay a 2 % Hoomanz Tip added on top.</p>

      <div className="grid sm:grid-cols-2 gap-3 mt-10" data-testid="sell-options">
        {OPTIONS.map(({ id, to, icon: Icon, title, blurb, testid }) => (
          <Link key={id} to={user ? to : `/login?next=${encodeURIComponent(to)}`}
            className="card p-6 flex items-start gap-4 group hover:border-[#0057FF] transition-colors"
            data-testid={testid}>
            <span className="h-10 w-10 rounded-full bg-[#F5F5F5] group-hover:bg-[#0057FF] group-hover:text-white text-[#050505] inline-flex items-center justify-center transition-colors flex-shrink-0">
              <Icon size={17}/>
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px]">{title}</p>
              <p className="text-sm text-[#454545] mt-1">{blurb}</p>
            </div>
            <ArrowRight size={16} className="text-[#8C8C8C] group-hover:text-[#0057FF] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1"/>
          </Link>
        ))}
      </div>

      {user && (
        <div className="mt-10 pt-8 border-t border-[#E8E8E8]">
          <p className="overline mb-3">Manage what you&apos;ve already posted</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/market/mine" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-market">My listings</Link>
            <Link to="/rentals/mine" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-rentals">My rentals</Link>
            <Link to="/collabs" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-collabs">Collabs</Link>
            <Link to="/jobs/mine" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-jobs">My jobs</Link>
            <Link to="/jobs/applications" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-apps">Job applications</Link>
            <Link to="/orders" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-orders">Orders received</Link>
            <Link to="/settings/payouts" className="btn-secondary text-sm !py-2 !px-4" data-testid="sell-manage-payouts">Payout settings</Link>
          </div>
        </div>
      )}
    </div>
  );
}
